import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray, UntypedFormControl, ValidatorFn, AbstractControl } from '@angular/forms';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Subject } from 'rxjs';
import moment from 'moment';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { OperateurSitesService } from 'src/app/data/declaration/services/operateur-sites/operateur-sites.service';
import { SessionContext, NavigationContext } from 'src/app/core/services/config/app.settings';
import { Site } from 'src/app/data/declaration/models/site.model';
import { StockProduitService } from 'src/app/data/declaration/services/stock-produit/stock-produit.service';
import { StockProduit } from 'src/app/data/declaration/models/stock-produit.model';
import { SaisieConditionnement } from 'src/app/data/declaration/models/saisie-conditionnement.model';
import { Assemblage } from 'src/app/data/declaration/models/assemblage.model';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { Referentiel } from 'src/app/data/declaration/models/referentiel.model';
import { Contenant } from 'src/app/data/declaration/models/contenant.model';
import { MouvementsProduitsService } from 'src/app/data/declaration/services/mouvements/mouvements-produits.service';
import { Produit } from 'src/app/data/habilitation/models/produit.model';
import { UtilisateurTypeCode } from 'src/app/data/intervenant/models/enums/type-utilisateur.enum';
import { LoaderService } from '../../../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-saisir-conditionnement',
    templateUrl: './saisir-conditionnement.component.html',
    styleUrls: ['./saisir-conditionnement.component.scss'],
    standalone: false
})
export class SaisirConditionnementComponent implements OnInit, OnDestroy {
    @Input()
    public campagne: string;
    @Input()
    public syntheseProduit: Produit = new Produit();
    @Input()
    estCreation = true;
    @Input()
    conditionnementAModifier: SaisieConditionnement;

    public referentiel: Referentiel = new Referentiel();
    public sitesOperateur: Site[] = [];
    public stocksProduit: StockProduit[] = [];
    public stockCourant: StockProduit = Object.assign(new StockProduit(), { stock: 0 });
    
    millesimeActuel: number | 'Non millésimé' = 'Non millésimé';

    private readonly destroy$ = new Subject<void>();

    public formSaisieConditionnement: UntypedFormGroup;
    idOperateur: number;
    get produit() {
        return this.formSaisieConditionnement.get('produit');
    }
    get date() {
        return this.formSaisieConditionnement.get('date');
    }
    get logement() {
        return this.formSaisieConditionnement.get('logement');
    }
    get lot() {
        return this.formSaisieConditionnement.get('lot');
    }
    get volume() {
        return this.formSaisieConditionnement.get('volume');
    }
    get entreposage() {
        return this.formSaisieConditionnement.get('entreposage');
    }

    get volumeTotalValue() {
        let volumeTotal = 0;

        // Récupérer le volume de l'année en cours
        if (this.volume.value) {
            volumeTotal = this.volume.value;
        }

        // Additionner les volumes des assemblages
        (this.formSaisieConditionnement.get('assemblages') as UntypedFormArray).controls.forEach(assemblage => {
            const volumeAssemblage = assemblage.get('volume').value;
            if (volumeAssemblage) {
                volumeTotal += volumeAssemblage;
            }
        });

        return this.bypassFloatingPointPrecision(volumeTotal);
    }

    get displayAlertMillesime() {
        let volume = 0;

        try {
            if (this.volume.value) {
                volume = this.volume.value;
            }
        } catch {
            return false;
        }

        return volume < this.volumeTotalValue * 0.85;
    }

    getMillesimeActuel(): number | 'Non millésimé' {

          // Récupérer les assemblages
            const assemblages: Assemblage[] = [
                Object.assign(new Assemblage(), {
                    annee: +this.campagne,
                    volume: +this.volume.value
                })
            ];
            (this.formSaisieConditionnement.get('assemblages') as UntypedFormArray).controls.forEach(assemblage => {
                assemblages.push(
                    Object.assign(new Assemblage(), {
                        annee: assemblage.get('annee').value.annee,
                        volume: assemblage.get('volume').value
                    })
                );
            })

        const volumeTotal = assemblages.reduce(
            (total, assemblage) => total + assemblage.volume,
            0
        );

        if (volumeTotal === 0) {
            return 'Non millésimé';
        }

        // Regroupe les volumes si un même millésime apparaît plusieurs fois
        const volumesParMillesime = new Map<number, number>();

        assemblages.forEach(assemblage => {
            volumesParMillesime.set(
                assemblage.annee,
                (volumesParMillesime.get(assemblage.annee) || 0) + assemblage.volume
            );
        });

        let millesimeMajoritaire: number;
        let volumeMajoritaire = 0;

        volumesParMillesime.forEach((volume, annee) => {
            if (volume > volumeMajoritaire) {
                volumeMajoritaire = volume;
                millesimeMajoritaire = annee;
            }
        });

        const pourcentage = (volumeMajoritaire / volumeTotal) * 100;

        return pourcentage >= 85 ? millesimeMajoritaire : 'Non millésimé';
    }

    constructor(
        public readonly modal: NgbActiveModal,
        private readonly fb: UntypedFormBuilder,
        private readonly toastr: ToastrService,
        private readonly operateurSitesService: OperateurSitesService,
        private readonly stockProduitService: StockProduitService,
        private readonly referentielService: ReferentielService,
        private readonly translate: TranslateService,
        private readonly loaderService: LoaderService,
        private readonly mouvementsProduitsService: MouvementsProduitsService
    ) {}

    ngOnInit() {
        if (this.estCreation) {
            this.idOperateur =
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR
                    ? SessionContext.get('idIntervenant')
                    : NavigationContext.get('idOperateur');
        } else {
            this.idOperateur = this.conditionnementAModifier.idOperateur;
        }

        this.formSaisieConditionnement = this.fb.group({
            produit: [{ value: this.syntheseProduit.libelle, disabled: true }],
            date: [undefined, Validators.required],
            logement: [undefined, Validators.required],
            lot: [undefined, Validators.required],
            volume: [
                undefined,
                [Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/), this.positifStrictValidator, this.volumeCourantValidator.bind(this)]
            ],
            assemblages: this.fb.array([]),
            entreposage: [undefined, Validators.required],
            contenants: this.fb.array([], Validators.compose([this.contenantsValidator.bind(this)]))
        });

        this.formSaisieConditionnement.get('volume').valueChanges.subscribe(() => {
            const contenants = this.formSaisieConditionnement.get('contenants') as UntypedFormArray;
            contenants.controls.forEach((contenant: UntypedFormGroup) => contenant.get('volume').updateValueAndValidity());
        });

                 this.formSaisieConditionnement.valueChanges
                    .pipe(
                        startWith(this.formSaisieConditionnement.value),
                        takeUntil(this.destroy$)
                    )
                    .subscribe(() => {
                        this.millesimeActuel = this.getMillesimeActuel();
                    });

        this.loadData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    close() {
        this.modal.dismiss();
    }

    loadData() {
        // Charger les stocks, les contenants et les adresses
        forkJoin(
            this.stockProduitService.getStockByProductCodeAndMoveType(this.idOperateur, this.syntheseProduit.code, 'VRAC'),
            this.referentielService.getReferentiel(),
            this.operateurSitesService.getSitesOperateur(this.idOperateur)
        )
            .pipe(this.loaderService.operator())
            .subscribe(([stocks, ref, operateurs]) => {
                // Affecter les stocks
                stocks.forEach(stock => {
                    if (stock.annee.toString() === this.campagne) {
                        this.stockCourant = stock;
                    } else {
                        this.stocksProduit.push(stock);
                    }
                });
                // Affecter les contenants
                this.referentiel = ref;
                // Affecter les adresses
                this.sitesOperateur = operateurs;

                if (!this.estCreation) {
                    this.loadConditionnement();
                }
            });
    }

    loadConditionnement() {
        this.produit.setValue(this.conditionnementAModifier.produit.libelle);
        this.date.setValue({
            year: this.conditionnementAModifier.date.year(),
            month: this.conditionnementAModifier.date.month() + 1,
            day: this.conditionnementAModifier.date.date()
        });
        this.logement.setValue(this.conditionnementAModifier.logement);
        this.lot.setValue(this.conditionnementAModifier.numeroLot);
        this.volume.setValue(this.calculerVolume());
        this.entreposage.setValue(this.sitesOperateur.find(s => s.id === this.conditionnementAModifier.site.id));

        const annee = this.conditionnementAModifier.assemblages.reduce((max, cond) => (max.volume > cond.volume ? max : cond)).annee;
        const assemblages = this.formSaisieConditionnement.get('assemblages') as UntypedFormArray;
        this.conditionnementAModifier.assemblages
            .filter(ass => ass.annee !== annee)
            .forEach(assemblage => {
                let form: UntypedFormGroup = null;                
                 
                this.stocksProduit.find(stock => stock.annee === assemblage.annee).stock  += assemblage.volume;
                 
                form = this.fb.group({
                    annee: [
                        this.stocksProduit.find(stock => stock.annee === assemblage.annee),
                        [this.selectedYearsOrContenantValidator('assemblages', 'annee').bind(this)]
                    ],
                    volume: [
                        assemblage.volume,
                        [
                            Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/),
                            this.positifStrictValidator,
                            this.assemblageVolumeValidator,
                            Validators.required
                        ]
                    ]
                });



                assemblages.push(form);
            });

            this.stockCourant.stock += this.conditionnementAModifier.assemblages
            .find(ass => ass.annee === annee).volume;

        const contenants = this.formSaisieConditionnement.get('contenants') as UntypedFormArray;
        this.conditionnementAModifier.contenants.forEach(contenant => {
            let form: UntypedFormGroup = null;

            form = this.fb.group({
                type: [
                    this.referentiel.refContenantList.find(cont => cont.idContenant === contenant.idType),
                    [this.selectedYearsOrContenantValidator('contenants', 'type').bind(this)]
                ],
                nombre: [contenant.nombre, [Validators.pattern(/^[0-9]\d*$/), this.positifStrictValidator]],
                volume: [
                    contenant.volume,
                    [
                        Validators.pattern(/(^\d+$)|(^\d+\.\d{1,3}$)|(^\d+\,\d{1,3}$)/),
                        this.positifStrictValidator,
                        this.contenantValidator.bind(this),
                        Validators.required
                    ]
                ]
            });

            contenants.push(form);
        });
    }

    add(type: string) {
        const control = this.formSaisieConditionnement.get(type) as UntypedFormArray;
        control.push(this.init(type));
    }

    selectedYearsOrContenantValidator(formulaire: string, champ: string): ValidatorFn {
        return (control: UntypedFormControl): ValidatorFn | any => {
            const value = control.value;
            const controls = (this.formSaisieConditionnement.get(formulaire) as UntypedFormArray).controls;

            // Renvoyer invalid si le même contenant a été sélectionné deux fois
            let trouve = 0;
            let cpt = 0;
            while (trouve < 2 && cpt < controls.length) {
                trouve += controls[cpt++].get(champ).value == value ? 1 : 0;
            }

            return trouve >= 2 ? { invalid: true } : null;
        };
    }

    init(type: string): UntypedFormGroup {
        let form: UntypedFormGroup = null;

        if (type === 'assemblages') {
            form = this.fb.group({
                annee: [undefined, [this.selectedYearsOrContenantValidator(type, 'annee').bind(this)]],
                volume: [
                    undefined,
                    [
                        Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/),
                        this.positifStrictValidator,
                        this.assemblageVolumeValidator,
                        Validators.required
                    ]
                ]
            });
        } else if (type === 'contenants') {
            form = this.fb.group({
                type: [undefined, [this.selectedYearsOrContenantValidator(type, 'type').bind(this)]],
                nombre: [undefined, [Validators.pattern(/^[0-9]\d*$/), this.positifStrictValidator]],
                volume: [
                    undefined,
                    [
                        Validators.pattern(/(^\d+$)|(^\d+\.\d{1,3}$)|(^\d+\,\d{1,3}$)/),
                        this.positifStrictValidator,
                        this.contenantValidator.bind(this),
                        Validators.required
                    ]
                ]
            });

            // Calculer automatiquement le volume lorsque l'on modifie le nombre
            form.get('nombre')
                .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
                .subscribe(value => this.calculerVolumeContenant(value, form));

            // Calculer automatiquement le nombre lorsque l'on modifie le volume
            form.get('volume')
                .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
                .subscribe(value => this.calculerNombreContenant(value, form));
        }

        return form;
    }

    positifStrictValidator(control: UntypedFormControl) {
        const value = parseFloat(control.value);

        if (isNaN(value) && control.value) {
            return { invalid: true };
        }
        return control.value > 0 ? null : { invalid: true };
    }

    volumeCourantValidator(control: UntypedFormControl) {
        return control.value > this.stockCourant.stock ? { invalid: true } : null;
    }

    assemblageVolumeValidator(control: UntypedFormControl) {
        let result = null;

        if (control.value && control.parent) {
            result = control.value > control.parent.get('annee').value.stock ? { invalid: true } : null;
        }

        return result;
    }

    contenantValidator(control: UntypedFormControl) {
        return control.value > this.volumeTotalValue ? { invalid: true } : null;
    }

    contenantsValidator() {
        let result = null;
        let volumes = 0;
        let volumeTotal = 0;
        const arrayVolumes: AbstractControl[] = [];

        if (this.formSaisieConditionnement) {
            const form = this.formSaisieConditionnement.get('contenants') as UntypedFormArray;
            volumeTotal = this.volumeTotalValue;
            form.controls.forEach(control => {
                volumes += +control.value.volume;
                arrayVolumes.push(control);
            });
        }

        if (volumes > volumeTotal) {
            result = { invalid: true };
            arrayVolumes.forEach(control => {
                const errors = control.get('volume').errors || {};
                errors.incorrect = true;
                control.get('volume').setErrors(errors);
            });
        } else {
            arrayVolumes.forEach(control => {
                let errors = control.get('volume').errors || {};
                delete errors.incorrect;
                if (Object.keys(errors).length === 0) {
                    errors = null;
                }
                control.get('volume').setErrors(errors);
            });
        }

        return result;
    }

    remove(type: string, index: number) {
        const control = this.formSaisieConditionnement.get(type) as UntypedFormArray;
        control.removeAt(index);
    }

    calculerVolumeContenant(value: number, form: UntypedFormGroup) {
        value = Math.floor(value);
        form.patchValue({ nombre: value });

        if (value > 0) {
            const type = form.get('type').value;
            if (type) {
                form.patchValue({
                    volume: this.bypassFloatingPointPrecision(type.volumeContenant * value)
                });
            }
        }
    }

    calculerNombreContenant(value: number, form: UntypedFormGroup) {
        if (value > 0) {
            const type = form.get('type').value;
            if (type) {
                const nombre = Math.floor(this.bypassFloatingPointPrecision(value / type.volumeContenant));

                form.patchValue({
                    nombre: nombre.toString(),
                    volume: this.bypassFloatingPointPrecision(nombre * type.volumeContenant)
                });
            }
        }
    }

    calculerVolume(): number {
        let volume = this.conditionnementAModifier.volume;
        const assemblage = this.conditionnementAModifier.assemblages.filter(a => a.annee.toString() !== this.campagne);
        if (assemblage.length > 0) {
            assemblage.forEach(a => (volume -= a.volume));
        }
        return volume;
    }

    submit() {
        if (this.formSaisieConditionnement.valid) {
            // Récupérer les assemblages
            const assemblages: Assemblage[] = [
                Object.assign(new Assemblage(), {
                    annee: +this.campagne,
                    volume: +this.volume.value
                })
            ];
            (this.formSaisieConditionnement.get('assemblages') as UntypedFormArray).controls.forEach(assemblage => {
                assemblages.push(
                    Object.assign(new Assemblage(), {
                        annee: assemblage.get('annee').value.annee,
                        volume: assemblage.get('volume').value
                    })
                );
            });

            // Récupérer les contenants
            const contenants: Contenant[] = [];
            (this.formSaisieConditionnement.get('contenants') as UntypedFormArray).controls.forEach(contenant => {
                contenants.push(
                    Object.assign(new Contenant(), {
                        idType: contenant.get('type').value.idContenant,
                        nombre: contenant.get('nombre').value,
                        volume: contenant.get('volume').value
                    })
                );
            });

            // Construire le conditionnement
            const conditionnement = Object.assign(new SaisieConditionnement(), {
                codeProduit: this.syntheseProduit.code,
                date: moment([this.date.value.year, this.date.value.month - 1, this.date.value.day]),
                logement: this.logement.value,
                numeroLot: this.lot.value,
                volume: +this.volumeTotalValue,
                idSite: this.entreposage.value.id,
                assemblages,
                contenants
            });

            if (this.estCreation) {
                // Envoyer le conditionnement à l'API
                this.mouvementsProduitsService.setConditionnement(this.idOperateur, this.campagne, conditionnement).subscribe(() => {
                    this.translate.get('page.declarations.synthese.modal.saisirConditionnement.update-ok').subscribe(msg => {
                        this.toastr.success('', msg);
                    });
                    this.modal.close();
                });
            } else {
                conditionnement.idMouvement = this.conditionnementAModifier.idMouvement;
                this.mouvementsProduitsService.majConditionnement(this.idOperateur, conditionnement).subscribe(() => {
                    this.translate.get('page.declarations.synthese.modal.saisirConditionnement.update-ok').subscribe(msg => {
                        this.toastr.success('', msg);
                    });
                    this.modal.close();
                });
            }
        }
    }

    getAssemblages() {
        return (this.formSaisieConditionnement.get('assemblages') as UntypedFormArray).controls;
    }

    getContenants() {
        return (this.formSaisieConditionnement.get('contenants') as UntypedFormArray).controls;
    }

    onTypeChange(i: number) {
        (this.formSaisieConditionnement.get(`contenants.${i}`) as UntypedFormControl).patchValue({ nombre: '', volume: '' });
    }

    private bypassFloatingPointPrecision(nombre: number): number {
        return Math.round(nombre * 1000) / 1000;
    }
}
